/* eslint-disable no-unused-vars, jsx-a11y/anchor-is-valid, jsx-a11y/alt-text, eqeqeq, react-hooks/exhaustive-deps */
// WeeklyCalendarUI.jsx
import { useHistory } from "react-router-dom";
import { useMemo } from "react";
import { React, useState, useRef, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import en from "date-fns/locale/en-GB";
import fr from "date-fns/locale/fr";
import es from "date-fns/locale/es";
import de from "date-fns/locale/de";
import it from "date-fns/locale/it";
import nl from "date-fns/locale/nl";
import pl from "date-fns/locale/pl";
import pt from "date-fns/locale/pt";
import cs from "date-fns/locale/cs";

import { Images } from '../Common/Images.jsx';
import NotificationPanel from "../Common/Notification.jsx";
import CheckInCheckOutList from "../Common/CheckInCheckOutList.jsx";

import Sidebar from "../Sidebar/Sidebar";

import "react-datepicker/dist/react-datepicker.css";

import { useTranslation } from 'react-i18next';
import {
    formatDateYYYYMMDD,
    convertTo24Hour,
    shouldBlockWeeklyCalendarTimedSlotClick
} from "../../utils/CommonUtils.js";
import CreditInfoCard from "../Common/CreditInfoCard.jsx";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";

registerLocale("en", en);
registerLocale("fr", fr);
registerLocale("es", es);
registerLocale("de", de);
registerLocale("it", it);
registerLocale("nl", nl);
registerLocale("pl", pl);
registerLocale("pt", pt);
registerLocale("cs", cs);

// Weekday column headers for the date picker, translated via the app's own i18n keys
// (covers every supported language, including those without a registered date-fns locale).
const DATEPICKER_WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// react-datepicker calls formatWeekDay once per column header, in display order. With
// calendarStartDay={1} that order is fixed Monday→Sunday, so each successive call maps to
// the matching app weekday key. A fresh counter is created per render; % 7 keeps the
// mapping correct regardless of how many times the picker re-renders its header.
const makeWeekdayHeaderFormatter = (t) => {
    let columnIndex = 0;
    return () => t(DATEPICKER_WEEKDAY_KEYS[columnIndex++ % 7]);
};

// FullCalendar weekday column headers, indexed by Date.getDay() (0 = Sunday). Translated via
// the app's own i18n keys because FullCalendar v6 formats names through native Intl, which has
// no Irish ("ga") data and would silently fall back to English.
const FC_WEEKDAY_HEADER_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function WeeklyCalendarUI({ isLoading, recordsData, events, loading, onDatesSet
    , selectedDate, setSelectedDate,
    zones, zoneDropdownOpen, setZoneDropdownOpen, selectedZoneId, setSelectedZoneId,
    handleSlotAction,
    onEventClick,
    holidays,
    onCheckInCheckout,
    onHeaderCheckInCheckout,
    mapAvailable
}) {

    const rootRef = useRef(null);
    const calendarRef = useRef(null);
    const [calendarEl, setCalendarEl] = useState(null);
    const [calendarKey, setCalendarKey] = useState(0);
    const [calendarTitle, setCalendarTitle] = useState(""); // Translated toolbar title (FullCalendar v6 has no Irish locale)
    const { t } = useTranslation("common");
    const { i18n } = useTranslation();
    const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
    const datePickerLocale = i18n.language.split("-")[0];
    const [UserFirstName, setUserFirstName] = useState(localStorage.getItem("firstname"));
    const [UserLastName, setUserLastName] = useState(localStorage.getItem("lastname"));
    const getInitials = (UserFirstName, UserLastName) => {
        return `${UserFirstName?.[0] || ""}${UserLastName?.[0] || ""}`.toUpperCase();
    };

    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    //console.log("getInitials-", getInitials)
    //const [isLoading, setIsLoading] = useState(false); // For initial full calendar load

    const toggle = (e) => {
        e.stopPropagation();                // prevent immediate close
        if (zones?.length > 0) setOpen((v) => !v);
    };
    const [open, setOpen] = useState(false);

    const [slotMinTime, setSlotMinTime] = useState(recordsData?.SpotMinStartTime || "00:00:00");
    const [slotMaxTime, setSlotMaxTime] = useState(recordsData?.SpotMaxEndTime || "24:00:00");
    //console.log("slotMinTime, slotMaxTime at weekcalendar", slotMinTime, slotMaxTime, recordsData?.SpotMinStartTime, recordsData?.SpotMaxEndTime)

    // Gray-out PAST TIME slots for the CURRENT DAY only, one 30-minute slot at a time
    // (matching the grid's slotDuration) so each elapsed slot is shaded individually
    // rather than as one continuous block.
    //
    // FullCalendar time lanes are shared across all 7 day-columns, so a lane CSS class
    // can't target a single day. Per-slot background events render per-column over the
    // exact past slots — same "fully elapsed" rule used by
    // shouldBlockWeeklyCalendarTimedSlotClick (now >= slot end).
    const calendarEvents = useMemo(() => {
        const baseEvents = Array.isArray(events) ? events : [];

        const zoneDate = recordsData?.ZoneCurrentDate
            ? String(recordsData.ZoneCurrentDate).slice(0, 10)
            : "";

        const toMinutes = (value) => {
            const [h, m] = String(value || "").split(":").map(Number);
            return Number.isFinite(h) && Number.isFinite(m) ? (h * 60) + m : null;
        };
        const toTimeString = (minutes) =>
            `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}:00`;

        const startMinutes = toMinutes(slotMinTime || "00:00:00");
        const maxMinutes = toMinutes(slotMaxTime || "24:00:00");
        const nowMinutes = toMinutes(recordsData?.ZoneCurrentTime);

        // Nothing to gray without a zone clock, or when "now" is at/before the grid start.
        if (!zoneDate || startMinutes === null || nowMinutes === null || nowMinutes <= startMinutes) {
            return baseEvents;
        }

        // Stop at the visible grid end so we never emit off-grid background events.
        const limitMinutes = maxMinutes !== null ? Math.min(nowMinutes, maxMinutes) : nowMinutes;

        const SLOT_MINUTES = 30; // matches the 30-min slotDuration shown in the week grid
        const pastSlotEvents = [];
        // Shade every slot that has FULLY elapsed: gray [t, t + 30) while t + 30 <= now.
        for (let t = startMinutes; t + SLOT_MINUTES <= limitMinutes; t += SLOT_MINUTES) {
            pastSlotEvents.push({
                start: `${zoneDate}T${toTimeString(t)}`,
                end: `${zoneDate}T${toTimeString(t + SLOT_MINUTES)}`,
                display: "background",
                classNames: ["fc-past-time"],
            });
        }

        return pastSlotEvents.length ? [...baseEvents, ...pastSlotEvents] : baseEvents;
    }, [events, recordsData?.ZoneCurrentDate, recordsData?.ZoneCurrentTime, slotMinTime, slotMaxTime]);
    const [isTimeGridLoading, setIsTimeGridLoading] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [pickerMonth, setPickerMonth] = useState(selectedDate || new Date());

    const userZoneCreditValue = useMemo(() => {
        return recordsData?.ZoneCreditValue ?? 0;
    }, [recordsData?.ZoneCreditValue]);

    const userZoneCreditBalance = useMemo(() => {
        return recordsData?.CreditBalance ?? "";
    }, [recordsData?.CreditBalance]);

    const creditBalanceRefillEvery = useMemo(() => {
        return recordsData?.CreditBalanceRefillEvery ?? "";
    }, [recordsData?.CreditBalance]);

    const isThumbnailPresent = useMemo(() => {
        return recordsData?.IsThumbnailPresent ?? "";
    }, [recordsData?.IsThumbnailPresent]);

    const companyId = useMemo(() => {
        return recordsData?.CompanyId ?? "";
    }, [recordsData?.CompanyId]);

    // Inclusive last bookable day from the API (local midnight, Safari-safe).
    // A bare `new Date("YYYY-MM-DD")` is parsed as UTC, which shifts the boundary
    // back and drops the last available day(s) — matching how MapDefault's picker
    // builds its maxDate (isLastDateBooking).
    const lastBookableDate = useMemo(() => {
        if (!recordsData?.LastDateOfBookingAvailable) {
            const fallback = new Date();
            fallback.setHours(0, 0, 0, 0);
            fallback.setDate(fallback.getDate() + 90);
            return fallback;
        }
        const lastDateOnly = String(recordsData.LastDateOfBookingAvailable).split(/[ T]/)[0];
        return new Date(`${lastDateOnly}T00:00:00`);
    }, [recordsData?.LastDateOfBookingAvailable]);

    // For Weekly calendar view, valid range is current date to LastDateOfBookingAvailable.
    // FullCalendar's validRange.end is EXCLUSIVE, so add one day to keep
    // LastDateOfBookingAvailable itself visible/navigable in the week grid.
    const calendarValidRange = useMemo(() => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);

        const end = new Date(lastBookableDate);
        end.setDate(end.getDate() + 1);

        return { start, end };
    }, [lastBookableDate]);

    // Datepicker should allow selecting date only up to LastDateOfBookingAvailable.
    // react-datepicker's maxDate is INCLUSIVE, so use the last bookable day directly.
    const datePickerMaxDate = useMemo(() => {
        return lastBookableDate;
    }, [lastBookableDate]);

    const isHolidayInList = (date) => {
        return holidays.includes(formatDateYYYYMMDD(date));
        // console.log("holidays from isHolidayInList")
    };

    const isSameMonthAndYear = (date1, date2) => {
        if (!date1 || !date2) return false;

        return (
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear()
        );
    };

    const shouldHideKeyboardSelection = !isSameMonthAndYear(selectedDate, pickerMonth);

    const formatPickerInputValue = (date) => {
        if (!date) return "";

        // Irish ("ga") has no Intl/date-fns data, so toLocaleDateString falls back to English
        // ("Mon 15 Jun"). Build the "EEE dd MMM" value from the app's own i18n keys for Irish
        // only; every other language keeps its working toLocaleDateString output.
        if (datePickerLocale === "ga") {
            const weekday = t(FC_WEEKDAY_HEADER_KEYS[date.getDay()]);
            const day = String(date.getDate()).padStart(2, "0");
            const englishMonth = new Intl.DateTimeFormat("en-US", { month: "long" })
                .format(date)
                .toLowerCase();
            const month = t(`month_3digit_of_${englishMonth}`);
            return `${weekday} ${day} ${month}`;
        }

        // Match MapDefault's "EEE dd MMM" display (e.g. "Mon 14 Nov"): translated short
        // weekday + 2-digit day + translated short month, in a fixed day-first order that
        // doesn't depend on the active locale's date ordering. Built from the app's own
        // i18n keys so every language (incl. Irish, which has no Intl/date-fns data)
        // renders consistently — the same keys drive the FullCalendar headers below.
        const weekday = t(FC_WEEKDAY_HEADER_KEYS[date.getDay()]);
        const day = String(date.getDate()).padStart(2, "0");
        const englishMonth = new Intl.DateTimeFormat("en-US", { month: "long" })
            .format(date)
            .toLowerCase();
        const month = t(`month_3digit_of_${englishMonth}`);
        return `${weekday} ${day} ${month}`;
    };
    const [showCheckInModal, setShowCheckInModal] = useState(false);

    const [checkInCheckOutRecords, setCheckInCheckOutRecords] = useState([]);

    useEffect(() => {
        if (recordsData?.CurrentDayBookingRecords) {
            setSlotMinTime(recordsData?.SpotMinStartTime);
            setSlotMaxTime(recordsData?.SpotMaxEndTime);
            setCheckInCheckOutRecords(recordsData.CurrentDayBookingRecords);
        }
    }, [recordsData]);

    const getCheckInSummary = (bookingRecords = []) => {
        const totalCount = bookingRecords.length;
        const pendingCheckInCount = bookingRecords.filter((booking) => {
            const flag = Number(booking?.CheckInFlag);
            return flag === 0 || flag === 2;
        }).length;
        const checkedInCount = bookingRecords.filter((booking) => {
            const flag = Number(booking?.CheckInFlag);
            return flag === 1;
        }).length;
        const shouldShowCheckIn = pendingCheckInCount > 0;
        const shouldShowCheckOut = totalCount > 0 && checkedInCount === totalCount;

        return {
            totalCount,
            pendingCheckInCount,
            checkedInCount,
            shouldShowCheckIn,
            shouldShowCheckOut,
        };
    };

    useEffect(() => {
        const frameId = requestAnimationFrame(() => {
            const calendarApi = calendarRef.current?.getApi();
            const btn = calendarApi?.el?.querySelector(".fc-checkInBtn-button");

            if (!btn) return;

            const currentDayBookingRecords = recordsData?.CurrentDayBookingRecords || [];
            const {
                totalCount,
                pendingCheckInCount,
                checkedInCount,
                shouldShowCheckIn,
                shouldShowCheckOut,
            } = getCheckInSummary(currentDayBookingRecords);

            if (totalCount === 0) {
                btn.classList.remove("active");
                btn.style.display = "none";
                return;
            }

            btn.style.display = "inline-block";

            if (shouldShowCheckIn) {
                btn.classList.remove("active");
                btn.innerHTML = totalCount > 1
                    ? `
                    ${t("react_check_in")}
                    <span class="color-orange">(${pendingCheckInCount})</span>
                `
                    : `${t("react_check_in")}`;
                return;
            }

            btn.innerHTML = totalCount > 1
                ? `
                ${t("react_check_out")}
                <span >(${shouldShowCheckOut ? checkedInCount : totalCount})</span>
            `
                : `${t("react_check_out")}`;
            btn.classList.add("active");
        });
        return () => cancelAnimationFrame(frameId);
    }, [recordsData?.CurrentDayBookingRecords, t]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setZoneDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [setZoneDropdownOpen]);

    const handleCheckInButtonClick = () => {
        const currentDayBookingRecords = recordsData?.CurrentDayBookingRecords || [];
        const { totalCount } = getCheckInSummary(currentDayBookingRecords);

        if (totalCount === 1) {
            onHeaderCheckInCheckout?.(currentDayBookingRecords[0]);
            return;
        }

        if (totalCount > 1) {
            setShowCheckInModal(true);
        }
    };

    const getSlotDurationMinutes = (calendarApi) => {
        const slotDuration = calendarApi?.getOption?.("slotDuration");

        if (typeof slotDuration === "string") {
            const [hours = 0, minutes = 0, seconds = 0] = slotDuration.split(":").map(Number);

            if ([hours, minutes, seconds].every(Number.isFinite)) {
                return (hours * 60) + minutes + Math.floor(seconds / 60);
            }
        }

        if (slotDuration && typeof slotDuration === "object") {
            const hours = Number(slotDuration.hours || 0);
            const minutes = Number(slotDuration.minutes || 0);
            const milliseconds = Number(slotDuration.milliseconds || 0);

            if ([hours, minutes, milliseconds].every(Number.isFinite)) {
                return (hours * 60) + minutes + Math.floor(milliseconds / 60000);
            }
        }

        return 30;
    };

    const eventContent = (eventInfo) => {
        //console.log("fullcalendar eventContent - ", eventInfo);
        const { title, start, end, allDay, extendedProps } = eventInfo.event;
        //console.log("allDay from eventcontent for date -", allDay, extendedProps.BookingDate)
        const startTime = start?.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });

        const endTime = end?.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
        // ✅ hide time if all-day OR 00:00 - 00:00
        const shouldShowTime =
            !allDay &&
            startTime !== "00:00" &&
            endTime !== "00:00";
        const { eventType } = extendedProps;
        return (
            <div >
                <div className="fc-booking-event">

                    {extendedProps.meetingName != "" && (
                        // The cell still shows the clipped name exactly as before;
                        // the second span is the hover tooltip carrying the full
                        // text, which matters now that a meeting name can be 255
                        // characters (RON-5953). Rendered unconditionally rather
                        // than only when truncated: detecting truncation means
                        // measuring scrollWidth per event on every render, which
                        // is real work in a week grid full of events.
                        <div className="fc-booking-title">
                            <div className="fc-booking-title-text"><span>{extendedProps.meetingName}</span></div>
                            <span className="meeting-name-tooltip">{extendedProps.meetingName}</span>
                        </div>
                    )
                    }
                    {(extendedProps.spotId != "-1" && extendedProps.spotName != "") && (
                        <div className="fc-booking-spot">
                            {extendedProps.spotName}
                        </div>
                    )}
                    {(shouldShowTime && startTime != "" && endTime != "") && (
                        <div className="fc-booking-time">
                            <img src={Images.TimeSlotClock} />
                            {convertTo24Hour(startTime)} - {convertTo24Hour(endTime)}
                        </div>
                    )}
                    {/* Join In Queue Icon */}
                    {(allDay && eventType === "JOIN_QUEUE") && (
                        <div className="joinqueue-icon">
                            <span className="joinqueue-tooltip">{t("react_join_to_queue")}</span>
                            <img src={Images.UserInJoinQueue} alt="Join Queue" />
                        </div>
                    )}
                    {/* Leave Queue Icon */}
                    {(allDay && eventType === "LEAVE_QUEUE") && (
                        <div className="joinqueue-icon" >
                            <span className="joinqueue-tooltip">{t("react_you_are_on_waitlist")}</span>
                            <a style={{ color: "var(--color-theme-green)" }}>
                                {recordsData?.ShowWaitlistPosition === 0 &&
                                    (extendedProps.employeeQueueCount != "0" ? `+${extendedProps.employeeQueueCount}` : "")
                                }
                            </a>
                            <img src={Images.UserInQue} alt="Leave Queue" />
                            <a style={{ color: "var(--color-theme-green)" }}>
                                {recordsData?.ShowWaitlistPosition === 1 &&
                                    (extendedProps.queuePosition != "0" ? extendedProps.queuePosition : "")
                                }
                            </a>
                        </div>
                    )}
                </div>
            </div>

        );
    }

    const employeeOptions = [
        "Michael Furey",
        "John Warde",
        "Mathieu Pegeot",
        "Daire O Brien",
        "Angela Hernandez",
        "New User",
        "One more Added"
    ];

    const history = useHistory();
    // Date-wide “Map + Spot not available”

    const disabledForNoSpotNoMap = useMemo(() => {
        const result = new Map();

        if (!recordsData?.Schedule) return result;

        recordsData.Schedule.forEach(day => {
            if (Number(day.SpotAvailable) === 0) {
                const dateKey = day.BookingDate?.split("T")[0];
                result.set(dateKey, day);
            }
        });

        //console.log("disabledForNoSpotNoMap", [...result.keys()]);
        return result;
    }, [recordsData]);

    useEffect(() => {
        if (!recordsData) return;

        const dayCells = document.querySelectorAll(".fc-daygrid-day");

        dayCells.forEach((cell) => {
            const dateStr = cell.getAttribute("data-date");
            const isHoliday = holidays.includes(dateStr);


            const isInactive = disabledForNoSpotNoMap.has(dateStr);
            if (isHoliday) {
                return ["fc-holiday"];           // 🔴 All-day & Time-grid slots holiday
            }
            else if (isInactive) {
                cell.classList.add("fc-allday-inactive");
            } else {
                cell.classList.remove("fc-allday-inactive");
                cell.classList.remove("fc-holiday");
            }
        });

    }, [recordsData, disabledForNoSpotNoMap]);


    //for (holiday + map + slot type)
    const isDateDisabled = (dateStr, isAllday) => {
        const schedule = recordsData?.Schedule?.find((d) => d.BookingDate === dateStr);
        const isHoliday = Array.isArray(holidays) && holidays.includes(dateStr);
        const hasBookings = (schedule?.Bookings?.length || 0) > 0;
        const allDayUnavailable = recordsData?.MapExist === 0 && schedule?.DeskAvailable === 0;
        //console.log("(schedule?.Bookings?.length -) -- ", schedule?.Bookings?.length);
        //console.log("weeklycalenderUI - dateStr, isAllday,recordsData?.MapExist, schedule?.SpotAvailable,schedule?.DeskAvailable,schedule?.MeetingRoomAvailable,schedule.HourlySpotAvailable  -- ", dateStr, isAllday, recordsData?.MapExist, schedule?.SpotAvailable, schedule?.DeskAvailable, schedule?.MeetingRoomAvailable, schedule?.HourlySpotAvailable)
        const grayOut = (schedule?.Bookings?.length === 0 && ((recordsData?.MapExist === 0 && schedule?.SpotAvailable === 0) ||      // if Map not exist and any spot is not available
            (isAllday === true && allDayUnavailable) // for Allday slot, if Map not exist and desk r not available
            || (isAllday === false && recordsData?.MapExist === 0))
        );    // for below time-slots, If map is not exist
        return isHoliday || grayOut;
    };
    const scrollStateRef = useRef(0);

    const preserveCalendarScroll = () => {
        const api = calendarRef.current?.getApi();
        if (!api) return;

        const scroller = api.el.querySelector(".fc-scroller-liquid-absolute");
        if (!scroller) return;

        scrollStateRef.current = scroller.scrollTop;
    };


    // Full meeting name tooltip (RON-5953): placement and visibility.
    //
    // What the reader sees is a SINGLE element on document.body, not the span inside each
    // event. The span stays put as the text source and is never displayed. Two reasons:
    //
    // 1. A tooltip inside the event is trapped twice over - clipped by FullCalendar's
    //    time-grid scroller, and painted under the all-day section because the event
    //    harness is a stacking context of its own. `position: fixed` fixes the first and
    //    cannot fix the second. See the rule in main-weekcalendar.css.
    // 2. Relocating the event's own span to the body would hand a React-owned node to
    //    someone else. FullCalendar re-renders event content freely, and React would later
    //    update or unmount a node that is no longer where it left it.
    //
    // ONE delegated listener on the grid, not a handler per event: a week can hold many
    // events, and per-event React state would re-render the whole grid on every hover.
    // Nothing here touches state - it writes to the DOM directly, and measures once per
    // hover rather than once per render.
    useEffect(() => {
        if (!calendarEl) return undefined;

        // Clearance between the event and the tooltip, and the minimum gap it keeps from
        // the edges of the window.
        const TOOLTIP_GAP_PX = 8;
        const VIEWPORT_MARGIN_PX = 8;
        // How far the tail's centre must stay from the bubble's left and right ends.
        // The bubble's corner radius is 8px and the tail is 7px either side of its
        // centre, so anything under 15 would let the tail poke out of a curve.
        const TAIL_EDGE_INSET_PX = 15;

        const tooltip = document.createElement("div");
        tooltip.className = "meeting-name-tooltip-floating";
        document.body.appendChild(tooltip);

        let activeTitle = null;

        const hideTooltip = () => {
            if (!activeTitle) return;

            tooltip.classList.remove("is-visible");
            activeTitle = null;
        };

        const showTooltip = (titleEl) => {
            const text = titleEl.querySelector(".meeting-name-tooltip")?.textContent?.trim();

            if (!text) return;

            tooltip.textContent = text;
            // Visible BEFORE measuring: a `display: none` element has no box, so its
            // height would read as 0 and the flip below could never trigger.
            tooltip.classList.add("is-visible");
            activeTitle = titleEl;

            const anchor = titleEl.getBoundingClientRect();
            const box = tooltip.getBoundingClientRect();

            // Never rise above the time grid's own scrollport. Above it sits the ALL DAY
            // row, and a tooltip covering that hides bookings the reader did not ask to
            // hide. Falls back to the window edge when the scroller cannot be found.
            const scroller = calendarEl.querySelector(".fc-scroller-liquid-absolute");
            const upperBound = Math.max(
                VIEWPORT_MARGIN_PX,
                scroller ? scroller.getBoundingClientRect().top : VIEWPORT_MARGIN_PX
            );

            // Above by preference, which is where the design puts it; below when there is
            // no room above it - the topmost-row case this whole thing exists for.
            const topIfAbove = anchor.top - box.height - TOOLTIP_GAP_PX;
            const placedAbove = topIfAbove >= upperBound;
            const top = placedAbove ? topIfAbove : anchor.bottom + TOOLTIP_GAP_PX;

            // Keep it on screen horizontally: a 240px tooltip on the last day column would
            // otherwise run off the right edge. Math.max on the upper bound guards the
            // degenerate case of a window narrower than the tooltip itself.
            const maxLeft = window.innerWidth - box.width - VIEWPORT_MARGIN_PX;
            const left = Math.min(
                Math.max(anchor.left, VIEWPORT_MARGIN_PX),
                Math.max(VIEWPORT_MARGIN_PX, maxLeft)
            );

            // The tail sits on whichever edge faces the event.
            tooltip.classList.toggle("is-above", placedAbove);
            tooltip.classList.toggle("is-below", !placedAbove);

            // Where the event's centre falls INSIDE the bubble. This is the whole trick:
            // the bubble's own left edge moves (it is clamped away from the window edge
            // on the last day column), so a tail fixed at 50% would drift off the event.
            // Expressing it relative to the bubble keeps the tail on the event wherever
            // the bubble ended up.
            const anchorCentreX = anchor.left + anchor.width / 2;
            const tailCentre = anchorCentreX - left;

            // Outside the bubble entirely - only reachable on a window narrower than the
            // tooltip, or an extreme corner cell. A tail pointing at empty space reads as
            // broken, so there simply is not one.
            const hasTail = tailCentre >= 0 && tailCentre <= box.width;

            tooltip.classList.toggle("has-tail", hasTail);

            if (hasTail) {
                const maxTail = Math.max(TAIL_EDGE_INSET_PX, box.width - TAIL_EDGE_INSET_PX);
                const tailLeft = Math.min(Math.max(tailCentre, TAIL_EDGE_INSET_PX), maxTail);

                tooltip.style.setProperty("--arrow-left", `${Math.round(tailLeft)}px`);
            }

            tooltip.style.left = `${Math.round(left)}px`;
            tooltip.style.top = `${Math.round(top)}px`;
        };

        const handleMouseOver = (event) => {
            const titleEl = event.target?.closest?.(".fc-booking-title");

            if (!titleEl) return;
            // Moving within the title that is already showing - nothing to redo.
            if (titleEl === activeTitle) return;

            showTooltip(titleEl);
        };

        const handleMouseOut = (event) => {
            const titleEl = event.target?.closest?.(".fc-booking-title");

            if (!titleEl) return;
            // A move to a child of the same title is not a leave. The tooltip is
            // `pointer-events: none`, so it never becomes the related target itself.
            if (event.relatedTarget && titleEl.contains(event.relatedTarget)) return;

            hideTooltip();
        };

        // Fixed coordinates go stale the moment anything moves, and the pointer can sit
        // still while the grid scrolls under it (wheel over an event), so a leave event
        // may never arrive. Dropping the tooltip is cheaper and less jarring than tracking
        // it. Capture phase, because the time grid's own scroll does not bubble.
        calendarEl.addEventListener("mouseover", handleMouseOver);
        calendarEl.addEventListener("mouseout", handleMouseOut);
        window.addEventListener("scroll", hideTooltip, true);
        window.addEventListener("resize", hideTooltip);

        return () => {
            calendarEl.removeEventListener("mouseover", handleMouseOver);
            calendarEl.removeEventListener("mouseout", handleMouseOut);
            window.removeEventListener("scroll", hideTooltip, true);
            window.removeEventListener("resize", hideTooltip);
            tooltip.remove();
        };
    }, [calendarEl]);

    useEffect(() => {
        const api = calendarRef.current?.getApi();
        if (!api) return;

        requestAnimationFrame(() => {
            const scroller = api.el.querySelector(".fc-scroller-liquid-absolute");
            if (scroller) {
                scroller.scrollTop = scrollStateRef.current;
            }
        });
    }, [events]);

    // Irish-only month/year header for the date-picker popup. react-datepicker has no "ga"
    // date-fns locale, so its default header renders English ("June 2026"). The stable English
    // month name is the i18n key (e.g. "June" -> "Meitheamh"); nav buttons reuse react-datepicker's
    // own classes for native styling/positioning.
    const renderIrishDatePickerHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => {
        const englishMonth = new Intl.DateTimeFormat("en-US", { month: "long" }).format(date);
        return (
            <div className="react-datepicker__current-month">
                <button
                    type="button"
                    aria-label="Previous Month"
                    className="react-datepicker__navigation react-datepicker__navigation--previous"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous" />
                </button>
                <span>{`${t(englishMonth)} ${date.getFullYear()}`}</span>
                <button
                    type="button"
                    aria-label="Next Month"
                    className="react-datepicker__navigation react-datepicker__navigation--next"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next" />
                </button>
            </div>
        );
    };

    const headerTitle = t("react_hi").replace("%s", UserFirstName);

    return (

        <div>

            <div className="content-area">
                <header className="header">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-md-2">
                                <div className="greeting-title">
                                    <p>{headerTitle}</p>
                                </div>
                            </div>
                            <div className="col-md-8">
                                <form action="" className="d-flex justify-content-center custom-inline-dropdown gap-3">

                                    <div className="filter-panel weekly-filter d-flex justify-content-end align-items-center gap-3">
                                        <div className="filter-options d-flex gap-3 w-100">

                                            <div className="booking-date header-date align-items-center d-flex">
                                                <label htmlFor="datepicker2" >
                                                    <p className="sub-dropdown-name">{t("date")}</p>

                                                    <DatePicker
                                                        // id="datepicker2"
                                                        locale={datePickerLocale}
                                                        formatWeekDay={makeWeekdayHeaderFormatter(t)}
                                                        renderCustomHeader={
                                                            datePickerLocale === "ga"
                                                                ? renderIrishDatePickerHeader
                                                                : undefined
                                                        }
                                                        selected={isSameMonthAndYear(selectedDate, pickerMonth) ? selectedDate : null}
                                                        value={formatPickerInputValue(selectedDate)}
                                                        openToDate={pickerMonth}
                                                        calendarClassName={shouldHideKeyboardSelection ? "datepicker-hide-keyboard-selection" : ""}
                                                        open={isDatePickerOpen}
                                                        dateFormat="EEE dd MMM"
                                                        onChangeRaw={(e) => e.preventDefault()}
                                                        onInputClick={() => {
                                                            setPickerMonth(selectedDate || new Date());
                                                            setIsDatePickerOpen((prev) => !prev);
                                                        }}
                                                        onClickOutside={() => setIsDatePickerOpen(false)}
                                                        onCalendarClose={() => setIsDatePickerOpen(false)}
                                                        shouldCloseOnSelect={true}
                                                        onMonthChange={(date) => {
                                                            setPickerMonth(date);
                                                        }}
                                                        onChange={(date) => {
                                                            if (!date) return;

                                                            setSelectedDate(date);
                                                            setPickerMonth(date);
                                                            setIsDatePickerOpen(false);

                                                            calendarRef.current
                                                                ?.getApi()
                                                                .gotoDate(date);
                                                        }}
                                                        minDate={new Date()}
                                                        maxDate={datePickerMaxDate}
                                                        calendarStartDay={1}
                                                        dayClassName={(date) =>
                                                            isHolidayInList(date) ? "react-datepicker__day--holidayClass" : undefined
                                                        }

                                                    />

                                                </label>
                                            </div>
                                            <div
                                                ref={rootRef}
                                                className="employee-list location-list"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ position: "relative", overflow: "visible", zIndex: 1 }}
                                            >
                                                <div className="dropdown form-input-container d-flex justify-content-center flex-column">
                                                    <button
                                                        className="dropdown-toggle position-relative custom-dropdown-btn"
                                                        type="button"
                                                        id="emp-dropdown-btn"
                                                        name="empdropdown"
                                                        onClick={() => setZoneDropdownOpen((s) => !s)}
                                                        aria-expanded={zoneDropdownOpen}
                                                    >
                                                        <p className="sub-dropdown-name">{t("zone")}</p>
                                                        <span>
                                                            {zones.find((z) => String(z.Id) === String(selectedZoneId))?.ZoneName || t("select_a_zone")}

                                                        </span>
                                                    </button>

                                                    <ul
                                                        className={`dropdown-menu custom-dropdown ${zoneDropdownOpen ? "show" : ""
                                                            }`}
                                                        aria-labelledby="emp-dropdown-btn"
                                                        id="zone-dropdown"
                                                    >
                                                        {zones.length === 0 ? (
                                                            <div style={{ padding: "8px 12px", color: "#9ca3af" }}>
                                                                {t("no_zones_available")}
                                                            </div>
                                                        ) : (
                                                            zones.map((zone) => (
                                                                <li
                                                                    key={zone.Id}
                                                                    className={selectedZoneId === zone.Id ? "selected" : ""}
                                                                    onClick={() => {
                                                                        setSelectedZoneId(zone.Id);
                                                                        setZoneDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className="dropdown-item"
                                                                    >
                                                                        {zone.ZoneName}
                                                                    </button>
                                                                </li>
                                                            ))
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* </div> */}

                                    <div className="calendar-wrapper">

                                        {/* 🔁 Switch stays on top */}
                                        <div className="calendar-header">
                                            <div className="calendar-switcher justify-content-start w-auto">
                                                <input
                                                    type="checkbox"
                                                    className="toggle"
                                                    id="toggle" checked={true} readOnly
                                                />

                                                <label htmlFor="toggle">

                                                    <span
                                                        className="off"
                                                        id="switch-to-month"
                                                        /* onClick={() => {
                                                            history.push("/calendar");
                                                        }} */
                                                        onClick={() => {
                                                            history.push("/calendar", {
                                                                selectedZoneId,
                                                            });
                                                        }}
                                                    /* onClick={() => {
                                                        history.push("/calendar", {
                                                            selectedZoneId,
                                                        });
                                                    }} */
                                                    >
                                                        {t("CalendarViewModeSwitcherMonthly")}
                                                    </span>

                                                    <span
                                                        className="on"
                                                        id="switch-to-week"
                                                        onClick={() => {
                                                            // Weekly → stay on this page
                                                        }}
                                                    >
                                                        {t("react_weekly_calendar")}
                                                        {/* Weekly */}
                                                    </span>
                                                </label>
                                            </div>


                                        </div>

                                    </div>

                                </form>
                            </div>

                            <div className="col-md-2">
                                <div className="menu-bar-options d-flex justify-content-end gap-3">
                                    {userZoneCreditValue !== 0 && (
                                        <CreditInfoCard
                                            userZoneCreditValue={userZoneCreditValue}
                                            userZoneCreditBalance={userZoneCreditBalance}
                                            creditBalanceRefillEvery={creditBalanceRefillEvery}
                                            IsThumbnailPresent={isThumbnailPresent}
                                            CompanyId={companyId}
                                        />
                                    )}
                                    <div className="notification-info">
                                        <a href="#" data-bs-toggle="offcanvas"
                                            data-bs-target="#notification-panel"
                                            aria-controls="notification-panel"
                                            className="position-relative notification-icon">
                                            <img src={Images.notification} alt="" />
                                            {hasUnreadNotifications && (
                                                <span className="orange-dot"></span>
                                            )}
                                        </a>

                                    </div>
                                    <NotificationPanel onUnreadChange={setHasUnreadNotifications} />

                                </div>
                            </div>
                        </div>
                    </div>
                </header>
                <div className="component-area">
                    {isLoading && (
                        <div id='preloader'>

                            <img src={Images.LoaderFull} alt="" />
                        </div>
                    )}
                    <Sidebar
                        IsThumbnailPresent={isThumbnailPresent}
                        CompanyId={companyId}
                    />
                    <div className="pt-96 my-container active-cont">
                        <div className="container">
                            {/* <HeaderFilter /> */}
                            <div ref={setCalendarEl}>
                                <div className="p-4 relative weekly-view-sec">
                                    {recordsData?.AvailabilityCode === 497 ||
                                        recordsData?.AvailabilityCode === 602 ||
                                        recordsData?.AvailabilityCode === 628 ? (
                                        <div className="no-space-found text-center my-5 py-5">
                                            <img
                                                src={Images.NoSpaceError}
                                                className="img-fluid mb-2 mt-5"
                                                alt=""
                                            />
                                            <p className="text-red fs-5">
                                                {recordsData?.AvailabilityMessage}
                                            </p>
                                        </div>
                                    ) : (
                                        <FullCalendar
                                            locale={i18n.language}
                                            ref={calendarRef}
                                            plugins={[timeGridPlugin, interactionPlugin]}
                                            initialView="customWeek"
                                            allDaySlot={true}
                                            allDayText={t("all_day")}
                                            allDayClassNames="fc-all-day-row"
                                            validRange={calendarValidRange}

                                            dayHeaderClassNames={(arg) =>
                                                holidays.includes(arg.date.toLocaleDateString("en-CA"))
                                                    ? ["fc-office-close"]
                                                    : []
                                            }
                                            // Only for ALL-DAY row
                                            dayCellClassNames={(arg) => {
                                                //console.log("arg.isAllDay - dayCellClassNames", arg.isAllDay, arg)
                                                const dateStr = arg.date.toLocaleDateString("en-CA");
                                                //const day = disabledForNoSpotNoMap.get(dateStr);
                                                const isInactive = disabledForNoSpotNoMap.has(dateStr);
                                                const isHoliday = holidays.includes(dateStr);

                                                if (isHoliday) {
                                                    return ["fc-holiday"];           // 🔴 All-day & Time-grid slots holiday
                                                }

                                                const isMapMissing = Number(recordsData?.MapExist) === 0;
                                                //const isNoSpot = day && Number(day.SpotAvailable) === 0;
                                                //console.log("dateStr ,isInactive, isMapMissing ", dateStr, isInactive, isMapMissing)
                                                if (isMapMissing || isInactive) {
                                                    //console.log("Gray out:", dateStr, { isMapMissing, isNoSpot });
                                                    return ["fc-inactive"];         // ⚪ Gray out day // only for time-grid slots
                                                }


                                                return [];
                                            }}

                                            views={{
                                                customWeek: {
                                                    type: "timeGridWeek",
                                                    duration: { days: 7 },
                                                },
                                            }}

                                            height="100vh"
                                            slotMinTime={slotMinTime}
                                            slotMaxTime={slotMaxTime}
                                            scrollTime={false}

                                            events={calendarEvents}
                                            /* datesSet={
                                                onDatesSet
                                                
                                            }  */      // it fires when Week view loads & User clicks prev / next
                                            datesSet={(arg) => {
                                                onDatesSet(arg);

                                                // Translate the toolbar title (month[s] + year) via the app's own i18n.
                                                // FullCalendar v6 formats its built-in title through native Intl, which has
                                                // no Irish ("ga") data, so we render it ourselves via the calendarTitle
                                                // customButton. A week can span two months/years, so build the range like
                                                // FullCalendar would. Stable English month name is the i18n key (display-only).
                                                const englishMonth = (date) =>
                                                    new Intl.DateTimeFormat("en-US", { month: "long" }).format(date);
                                                const start = arg.view.currentStart;
                                                const end = new Date(arg.view.currentEnd.getTime() - 1); // currentEnd is exclusive
                                                const startMonth = t(englishMonth(start));
                                                const endMonth = t(englishMonth(end));
                                                const startYear = start.getFullYear();
                                                const endYear = end.getFullYear();

                                                let title;
                                                if (startYear !== endYear) {
                                                    title = `${startMonth} ${startYear} – ${endMonth} ${endYear}`;
                                                } else if (startMonth !== endMonth) {
                                                    title = `${startMonth} – ${endMonth} ${startYear}`;
                                                } else {
                                                    title = `${startMonth} ${startYear}`;
                                                }
                                                setCalendarTitle(title);
                                            }}


                                            eventClick={(info) => {// it fires when Clicking an already created event
                                                info.jsEvent.preventDefault();
                                                info.jsEvent.stopPropagation();

                                                const event = info.event;
                                                console.log("FullCalendar-----eventClick - ", event);

                                                const dateStr = event.start?.toLocaleDateString("en-CA");
                                                const isHoliday = Array.isArray(holidays) && holidays.includes(dateStr);

                                                if (isHoliday) {
                                                    return;
                                                }

                                                const isAllDayEvent =
                                                    event.allDay === true ||
                                                    event.extendedProps?.isFullDayFallback === true;

                                                if (shouldBlockWeeklyCalendarTimedSlotClick({
                                                    clickedDateTime: event.start,
                                                    clickedSlotEndDateTime: event.end,
                                                    isAllDay: isAllDayEvent,
                                                    zoneCurrentDate: recordsData?.ZoneCurrentDate,
                                                    zoneCurrentTime: recordsData?.ZoneCurrentTime,
                                                    spotMinStartTime: recordsData?.SpotMinStartTime,
                                                    spotMaxEndTime: recordsData?.SpotMaxEndTime
                                                })) {
                                                    return;
                                                }

                                                preserveCalendarScroll();
                                                onEventClick({ eventInfo: event });
                                            }}


                                            // For empty Time-grid slot click
                                            dateClick={(info) => {
                                                //console.log("FullCalendar----- dateClick - ", info);
                                                const dateStr = info.date.toLocaleDateString("en-CA");
                                                const calendarApi = calendarRef.current?.getApi();
                                                const slotDurationMinutes = getSlotDurationMinutes(calendarApi);
                                                const clickedSlotEndDateTime = new Date(
                                                    info.date.getTime() + (slotDurationMinutes * 60 * 1000)
                                                );

                                                if (isDateDisabled(dateStr, info.allDay)) return;
                                                // Check if the clicked slot should be blocked for PAST TIME of current day
                                                if (shouldBlockWeeklyCalendarTimedSlotClick({
                                                    clickedDateTime: info.date,
                                                    clickedSlotEndDateTime,
                                                    isAllDay: info.allDay,
                                                    zoneCurrentDate: recordsData?.ZoneCurrentDate,
                                                    zoneCurrentTime: recordsData?.ZoneCurrentTime,
                                                    spotMinStartTime: recordsData?.SpotMinStartTime,
                                                    spotMaxEndTime: recordsData?.SpotMaxEndTime
                                                })) return;
                                                //setSelectedDate(info.date);
                                                if (!calendarApi) return;

                                                handleSlotAction({
                                                    dateStr,
                                                    isAllDay: info.allDay
                                                });
                                            }}

                                            // Format Week Calendar Detail's View
                                            eventContent={eventContent}
                                            dayHeaderContent={(info) => {
                                                const dateStr = info.date.toISOString().split("T")[0]; // ✅ FIX
                                                // Translate the weekday name via the app's i18n keys (FullCalendar v6 uses
                                                // native Intl, which has no Irish data and falls back to English).
                                                const dayName = t(FC_WEEKDAY_HEADER_KEYS[info.date.getDay()]);
                                                const isHoliday = holidays.includes(dateStr);

                                                return (
                                                    <div className="weekly-header-cell">
                                                        {/* DAY NAME */}
                                                        <div className={`ctm-day-name ${isHoliday ? "holiday-text" : ""}`}>
                                                            {dayName}
                                                        </div>

                                                        {/* DATE CIRCLE */}
                                                        <div className={`ctm-date-circle ${isHoliday ? "holiday-circle" : ""}`}>
                                                            {info.date.getDate()}
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                            headerToolbar={{ left: 'checkInBtn', center: 'prev,calendarTitle,next', right: '' }}
                                            customButtons={{
                                                checkInBtn: {
                                                    //text: `Check In`,
                                                    click: handleCheckInButtonClick,
                                                },
                                                // React-owned, i18n-translated month/year title (replaces FullCalendar's
                                                // English-only built-in title). Non-interactive — click is a no-op.
                                                calendarTitle: {
                                                    text: calendarTitle,
                                                    click: () => { },
                                                },
                                            }}

                                            slotLabelContent={(arg) => {
                                                const hour = arg.date.getHours();
                                                const minute = arg.date.getMinutes().toString().padStart(2, '0');
                                                return `${hour}:${minute}`;
                                            }}
                                        />
                                    )}  
                                </div>
                            </div>
                        </div>
                    </div>

                    {showCheckInModal && (
                        <CheckInCheckOutList ZoneId={selectedZoneId} zoneName={zones.find((z) => z.Id === selectedZoneId)?.ZoneName}
                            checkInCheckOutRecords={recordsData?.CurrentDayBookingRecords ? recordsData?.CurrentDayBookingRecords : []}
                            setCheckInCheckOutRecords={setCheckInCheckOutRecords}
                            selectedZoneId={selectedZoneId} isLoading={isLoading}
                            closeModal={() => {
                                setShowCheckInModal(false);
                            }}
                            selectedDate={selectedDate}
                            onCheckInCheckout={onCheckInCheckout}
                            onCheckInApiResponse={async ({ response, zoneId, bookingDateStr, closeModal }) => {
                                const responseCode = Number(response?.Data?.ResponseCode);

                                if (responseCode !== 449) {
                                    return;
                                }

                                closeModal();
                                handleApiUiResponse(response, {
                                    successToaster: 0,
                                    errorToaster: 0,
                                    alertPopup: 1,
                                    onConfirmByCode: {
                                        449: async () => {
                                            await onCheckInCheckout?.(zoneId, bookingDateStr);
                                        }
                                    }
                                });
                            }}
                            mapAvailableType={recordsData?.MapType}
                            mapAvailable={mapAvailable}
                        />
                    )}
                    {renderApiUi(isLoading)}
                </div>
            </div>
        </div>
    );
}
export default WeeklyCalendarUI;


function Dropdown({ dropdownId, items, label }) {
    const [selectedItem, setSelectedItem] = useState(items[0]);

    const handleItemClick = (item) => {
        setSelectedItem(item);
    };

    return (
        <div className="employee-list">
            <div className="dropdown form-input-container d-flex justify-content-center flex-column">
                <button
                    className="dropdown-toggle form-control position-relative custom-dropdown-btn"
                    type="button"
                    id={dropdownId}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    name="empdropdown"
                    required
                >
                    <p className="sub-dropdown-name">{label}</p>
                    <span>{selectedItem}</span>
                </button>
                <ul className="dropdown-menu custom-dropdown" aria-labelledby={dropdownId}>
                    {items.map((item, index) => (
                        <li
                            key={index}
                            className={item === selectedItem ? "selected" : ""}
                            onClick={() => handleItemClick(item)}
                        >
                            <a className="dropdown-item">
                                {item}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>

    );
}
